import { useMemo, useState } from 'react';
import { CountryDropdown, RegionDropdown } from 'react-country-region-selector';
import DatePicker from 'react-datepicker';
import { IoMdArrowDropdown, IoMdArrowDropright } from 'react-icons/io';
import Button from '../../../components/ui/Button/Button';
import { Input } from '../../../components/ui/Input/Input';
import { Select } from '../../../components/ui/Select/Select';
import { RequiredField } from '../../../utils/components';
import { calcDate, marketplaces, validateCase } from '../../../utils/constants';
import CaseIssue from './CaseIssue';

const steps = [
  { id: 1, label: 'Personal Details' },
  { id: 2, label: 'Product Information' },
  { id: 3, label: 'Order Details' },
  { id: 4, label: 'Product Issue' },
];

const CaseSteps = ({ data, step, setStep }) => {
  const [formData, setFormData] = useState({
    first_name: data.first_name || '',
    last_name: data.last_name || '',
    email: data.email || '',
    country: data.country || '',
    state: data.state || '',
    city: data.city || '',
    zip: data.zip || '',
    address: data.address || '',
    order_number: data.order_number || '',
    marketplace: data.marketplace || '',
    purchase_date: data.purchase_date ? new Date(data.purchase_date) : '',
    product_id: data.product_id || '',
    category: data.category || '',
  });

  const [required, setRequired] = useState({
    first_name: { filled: false, message: '' },
    last_name: { filled: false, message: '' },
    email: { filled: false, message: '' },
    product_id: { filled: false, message: '' },
    category: { filled: false, message: '' },
    order_number: { filled: false, message: '' },
    marketplace: { filled: false, message: '' },
    purchase_date: { filled: false, message: '' },
  });

  const [isOpen, setOpen] = useState(false);

  const {
    country,
    state,
    first_name,
    last_name,
    email,
    city,
    zip,
    address,
    category,
    product_id,
    order_number,
    marketplace,
    purchase_date,
  } = formData;
  const selectCountry = (value) => setFormData({ ...formData, country: value });
  const selectRegion = (value) => setFormData({ ...formData, state: value });

  const changeStep = (e) => {
    const index = +e.currentTarget.getAttribute('data-index');
    if (index) {
      if (index < step) {
        setStep(index);
        return;
      }

      const data = validateCase({ old: required, values: formData, step });
      setRequired(data.newValues);

      if (!data.preventChange) setStep(index);
    }
  };

  const changeFormData = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const openAddress = (e) => {
    setOpen(!isOpen);
    if (!country) {
      setFormData({ ...formData, country: 'United States' });
    }
  };

  const setPurchaseDate = (date) =>
    setFormData({ ...formData, purchase_date: date });

  const currentStepLabel = steps.find((s) => s.id === step)?.label || '';

  const categoriesOptions = useMemo(() => {
    if (!data.category) return [];
    return [{ label: data.category, value: data.category }];
  }, [data.category]);

  const productsOptions = useMemo(() => {
    if (!data.product_id) return [];
    return [{ label: data.name, value: data.product_id }];
  }, [data.product_id, data.name]);

  const marketplacesOptions = useMemo(() => {
    return marketplaces.map((marketplace) => {
      return { value: marketplace, label: marketplace };
    });
  }, []);

  return (
    <div className='case-steps-box'>
      <div className='register-list'>
        <ul className='register-list-box'>
          {steps.map((s) => (
            <li
              key={s.id}
              className={`register-list-row ${
                step === s.id ? 'register-list-row-active' : ''
              }`}
              data-index={s.id}
              onClick={changeStep}
            >
              {s.label}
            </li>
          ))}
        </ul>
      </div>
      <div className='register-form'>
        <div
          className={`register-form-box ${
            step === 1 ? 'register-active-form' : ''
          }`}
        >
          <div className='register-form-box-body'>
            <h3 className='register-list-row-active'>{currentStepLabel}</h3>
            <ul className='register-form-body'>
              <li className='register-form-row'>
                <Input
                  type='text'
                  label='First Name'
                  value={first_name}
                  onChange={changeFormData}
                  name='first_name'
                  error={required.first_name.message}
                  className='case-form__input'
                  required
                />
              </li>
              <li className='register-form-row'>
                <Input
                  type='text'
                  label='Last Name'
                  value={last_name}
                  onChange={changeFormData}
                  name='last_name'
                  error={required.last_name.message}
                  className='case-form__input'
                  required
                />
              </li>
              <li className='register-form-row'>
                <Input
                  type='email'
                  label='Email'
                  value={email}
                  onChange={changeFormData}
                  name='email'
                  error={required.email.message}
                  className='case-form__input'
                  required
                />
              </li>
              <li className='register-form-row'>
                <div className='register-form-open' onClick={openAddress}>
                  Add an address
                  {isOpen ? (
                    <IoMdArrowDropdown className='add-address-icon' />
                  ) : (
                    <IoMdArrowDropright className='add-address-icon' />
                  )}
                </div>
              </li>
            </ul>
            <ul
              style={{ display: isOpen ? '' : 'none' }}
              className='register-form-sublist'
            >
              <li className='register-form-row register-form-address-row register-form-sublist--end'>
                <Input
                  type='text'
                  label='Address'
                  value={address}
                  onChange={changeFormData}
                  name='address'
                  className='register-form__input'
                />
              </li>
              <li className='register-form-row register-form-address-row register-form-sublist--end'>
                <Input
                  type='text'
                  label='City'
                  value={city}
                  onChange={changeFormData}
                  name='city'
                  className='register-form__input'
                />
              </li>
              <li className='register-form-row register-form-address-row register-form-sublist--end'>
                <label className='register-form-select__label'>
                  Province/State
                </label>
                <div className='register-form-select__wrapper'>
                  <RegionDropdown
                    blankOptionLabel='No Country selected'
                    defaultOptionLabel='Select a region'
                    country={country}
                    value={state}
                    onChange={selectRegion}
                    classes='register-input'
                  />
                  <IoMdArrowDropdown className='register-form-select-icon' />
                </div>
              </li>
              <li className='register-form-row register-form-address-row register-form-sublist--end'>
                <Input
                  type='text'
                  label='ZIP'
                  value={zip}
                  onChange={changeFormData}
                  name='zip'
                  className='register-form__input'
                />
              </li>
              <li className='register-form-row register-form-address-row register-form-sublist--end'>
                <label className='register-form-select__label'>Country</label>
                <div className='register-form-select__wrapper'>
                  <CountryDropdown
                    defaultOptionLabel='Select a Country'
                    value={country}
                    onChange={selectCountry}
                    classes='register-input'
                  />
                  <IoMdArrowDropdown className='register-form-select-icon' />
                </div>
              </li>
            </ul>
          </div>
          <div className='register-form-btns'>
            <Button
              data-index='2'
              onClick={changeStep}
              className='register-form-button'
            >
              NEXT
            </Button>
          </div>
        </div>
        <div
          className={`register-form-box ${
            step === 2 ? 'register-active-form' : ''
          }`}
        >
          <div className='register-form-box-body'>
            <h3 className='register-list-row-active'>{currentStepLabel}</h3>
            <ul className='register-form-body'>
              <li className='register-form-row'>
                <Select
                  label='Product Category'
                  name='category'
                  options={categoriesOptions}
                  value={category}
                  onChange={changeFormData}
                  placeholder='Select Category'
                  error={required.category.message}
                  containerClassName='register-form-product__select'
                  required
                />
              </li>
              <li className='register-form-row'>
                <Select
                  label='Product Name'
                  name='product_id'
                  options={productsOptions}
                  value={product_id}
                  onChange={changeFormData}
                  placeholder='Select Product'
                  error={required.product_id.message}
                  containerClassName='register-form-product__select'
                  required
                />
              </li>
            </ul>
          </div>

          <div className='register-form-btns'>
            <Button
              data-index='1'
              onClick={changeStep}
              variant='secondary'
              className='register-form-button'
            >
              BACK
            </Button>
            <Button
              data-index='3'
              onClick={changeStep}
              className='register-form-button'
            >
              NEXT
            </Button>
          </div>
        </div>
        <div
          className={`register-form-box ${
            step === 3 ? 'register-active-form' : ''
          }`}
        >
          <div className='register-form-box-body'>
            <h3 className='register-list-row-active'>{currentStepLabel}</h3>
            <ul className='register-form-body'>
              <li className='register-form-row'>
                <Select
                  label='Marketplace'
                  name='marketplace'
                  options={marketplacesOptions}
                  value={marketplace}
                  onChange={changeFormData}
                  placeholder='Select Marketplace'
                  error={required.marketplace.message}
                  containerClassName='register-form-product__select'
                  required
                />
              </li>
              <li className='register-form-row'>
                <label className='register-form-select__label'>
                  Purchase Date
                  <RequiredField />
                </label>
                <div className='date-picker-wrapper'>
                  <DatePicker
                    selected={purchase_date}
                    onChange={setPurchaseDate}
                    minDate={calcDate(60)}
                    maxDate={new Date()}
                    className={`register-input ${
                      required.purchase_date.filled
                        ? 'register-input-error'
                        : ''
                    }`}
                    placeholderText='Select a date'
                    calendarClassName='custom-calendar'
                  />
                </div>
                <p className='register-error-message'>
                  {required.purchase_date.message}
                </p>
              </li>
              <li className='register-form-row'>
                <Input
                  label='Order Number'
                  name='order_number'
                  type='text'
                  value={order_number}
                  onChange={changeFormData}
                  error={required.order_number.message}
                  className='register-form__input'
                  required
                />
              </li>
            </ul>
          </div>
          <div className='register-form-btns'>
            <Button
              data-index='2'
              onClick={changeStep}
              variant='secondary'
              className='register-form-button'
            >
              BACK
            </Button>
            <Button
              data-index='4'
              onClick={changeStep}
              className='register-form-button'
            >
              NEXT
            </Button>
          </div>
        </div>
        <CaseIssue
          changeStep={changeStep}
          data={formData}
          step={step}
          warrantyData={data}
        />
      </div>
    </div>
  );
};

export default CaseSteps;
